import { useState } from "react";
import { ChefHat, Eye, LayoutGrid, Tags, ShieldCheck, ClipboardList, AlertCircle, BarChart3, FlaskConical, Star } from "lucide-react";
import { useResource, HubRow, SubHeader, StatTile, EmptyState } from "./Shared";
import { ReviewsScreen } from "./CreatorsTab";

const HUB = [
  { key: "creator-types", icon: ChefHat, title: "Creator Types", subtitle: "The kinds of food creators allowed on Dishly" },
  { key: "discover-rows", icon: LayoutGrid, title: "Discover Rows", subtitle: "The sections customers see on their home feed" },
  { key: "food-types", icon: Tags, title: "Food Types & Dietary Tags", subtitle: "Cuisines and dietary labels in use" },
  { key: "moderation", icon: ClipboardList, title: "Moderation Queue", subtitle: "Listings waiting for review" },
  { key: "disputes", icon: AlertCircle, title: "Order Disputes", subtitle: "Reviews disputed by creators" },
  { key: "growth", icon: BarChart3, title: "Growth & Feedback", subtitle: "Referral sources and app feedback" },
  { key: "preview", icon: Eye, title: "Preview Creators", subtitle: "See the app as a customer" },
  { key: "compliance-rules", icon: ShieldCheck, title: "Compliance Rules", subtitle: "Permit and ID requirements per creator type" },
  { key: "compliance", icon: ShieldCheck, title: "Compliance Overview", subtitle: "Document status across all creators" },
  { key: "demo", icon: FlaskConical, title: "Demo & Test Data", subtitle: "Seed and demo content controls" },
];

function CreatorTypes({ onBack }) {
  const { data, loading } = useResource("discover-rows");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const types = data.options?.creator_types || [];
  return (
    <div data-testid="creator-types-screen">
      <SubHeader title="Creator Types" subtitle="The kinds of food creators allowed on Dishly" onBack={onBack} />
      <div className="space-y-2.5">
        {types.length === 0 ? <EmptyState icon={ChefHat} text="No creator types defined." /> :
          types.map((t) => (
            <div key={t.key} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-4">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-terracotta/10 text-terracotta"><ChefHat className="h-4 w-4" /></span>
              <span className="text-[15px] font-bold text-charcoal">{t.label}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

function DiscoverRows({ onBack }) {
  const { data, loading } = useResource("discover-rows");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="discover-rows-screen">
      <SubHeader title="Discover Rows" subtitle="Sections on the customer home feed" onBack={onBack} />
      <div className="space-y-2.5">
        {data.rows.length === 0 ? <EmptyState icon={LayoutGrid} text="No discover rows configured." /> :
          data.rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`discover-row-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-[15px] font-bold text-charcoal">{r.title}</p>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${r.enabled ? "bg-emerald-100 text-emerald-700" : "bg-charcoal/5 text-charcoal/50"}`}>
                  {r.enabled ? "Enabled" : "Hidden"}
                </span>
              </div>
              <p className="mt-1 text-[13px] capitalize text-charcoal/55">{r.kind}</p>
              {(r.cuisines?.length > 0 || r.keywords?.length > 0) && (
                <p className="mt-1 text-xs text-charcoal/45">{[...(r.cuisines || []), ...(r.keywords || [])].join(" · ")}</p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

function FoodTypes({ onBack }) {
  const { data, loading } = useResource("taxonomy");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const Section = ({ title, items, usage, testId }) => (
    <div data-testid={testId}>
      <h3 className="mb-2.5 text-xs font-bold uppercase tracking-widest text-charcoal/40">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((c) => (
          <span key={c} className="rounded-full border border-black/5 bg-white px-3.5 py-2 text-[13px] font-semibold text-charcoal">
            {c}{usage?.[c] != null && <span className="ml-1.5 text-charcoal/40">{usage[c]}</span>}
          </span>
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="food-types-screen" className="space-y-6">
      <SubHeader title="Food Types & Dietary Tags" onBack={onBack} />
      <Section title="Cuisines" items={data.cuisines} usage={data.usage?.cuisines} testId="cuisines-section" />
      <Section title="Dietary tags" items={data.dietary} usage={data.usage?.dietary} testId="dietary-section" />
    </div>
  );
}

function Growth({ onBack }) {
  const { data, loading } = useResource("growth");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="growth-screen">
      <SubHeader title="Growth & Feedback" subtitle="Where users come from and what they say" onBack={onBack} />
      <div className="grid grid-cols-3 gap-2.5">
        <StatTile n={data.feedback_count} label="Feedback sent" testId="growth-feedback-count" />
        <StatTile n={data.feedback_avg != null ? Number(data.feedback_avg).toFixed(1) : "—"} label="Avg rating" testId="growth-feedback-avg" />
        <StatTile n={data.prompted_count} label="Prompted" testId="growth-prompted" />
      </div>
      <h3 className="mb-2.5 mt-6 text-xs font-bold uppercase tracking-widest text-charcoal/40">Referral sources</h3>
      <div className="space-y-2.5">
        {data.referral_sources.length === 0 ? <EmptyState icon={BarChart3} text="No referral data yet." /> :
          data.referral_sources.map((r) => (
            <div key={r.source} className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4">
              <span className="text-sm font-bold capitalize text-charcoal">{(r.source || "unknown").replace(/_/g, " ")}</span>
              <span className="text-[13px] text-charcoal/60">{r.customers} customers · {r.creators} creators</span>
            </div>
          ))}
      </div>
    </div>
  );
}

function NoteScreen({ title, subtitle, onBack }) {
  return (
    <div>
      <SubHeader title={title} onBack={onBack} />
      <EmptyState icon={ShieldCheck} text={subtitle || "This area is managed in the Dishly app."} />
    </div>
  );
}

export default function ControlsTab({ screen, goScreen, go }) {
  const [inner, setInner] = useState(null);
  const active = screen || inner;
  const back = () => (screen ? goScreen(null) : setInner(null));
  const open = screen ? goScreen : setInner;

  if (active === "creator-types") return <CreatorTypes onBack={back} />;
  if (active === "discover-rows") return <DiscoverRows onBack={back} />;
  if (active === "food-types") return <FoodTypes onBack={back} />;
  if (active === "growth") return <Growth onBack={back} />;
  if (active === "disputes") return <ReviewsScreen onBack={back} onlyDisputes />;
  if (active === "compliance-rules") return <NoteScreen title="Compliance Rules" subtitle="Permit and ID requirements are managed in the Dishly app." onBack={back} />;
  if (active === "compliance") return <NoteScreen title="Compliance Overview" subtitle="Open a creator from the Creators tab to review their documents." onBack={back} />;
  if (active === "preview") return <NoteScreen title="Preview Creators" subtitle="Open the main site to see the app as a customer." onBack={back} />;
  if (active === "demo") return <NoteScreen title="Demo & Test Data" subtitle="Demo content is managed in the Dishly app." onBack={back} />;

  return (
    <div data-testid="controls-tab">
      <h2 className="font-display text-2xl font-bold text-charcoal">Controls</h2>
      <p className="mb-5 mt-0.5 text-sm text-charcoal/55">Platform settings and content</p>
      <div className="space-y-2.5">
        {HUB.map((h) => (
          <HubRow key={h.key} icon={h.icon} title={h.title} subtitle={h.subtitle} testId={`controls-hub-${h.key}`}
            onClick={() => h.key === "moderation" ? go("creators", "listings") : open(h.key)} />
        ))}
      </div>
    </div>
  );
}
